import React, { useState, useEffect } from "react";
import AdminDashboardOrder from "./AdminDashboardOrder";
import AdminGvg from "./AdminGvg";
import AdminPlanDashboard from "./AdminPlanDashboard";
import AdminOrderPlan from "./AdminOrderPlan";
import { chitOnboardByDateRange } from "../../api/PupilApi";
import { toast } from "react-toastify";

const Tabs = ({ color }) => {
  const [openTab, setOpenTab] = useState(1);
  const [chits, setChits] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [selectedChit, setSelectedChit] = useState(null);
  const [loading, setLoading] = useState(false);

  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Fetch chits by date range
  const fetchChits = async (start, end) => {
    try {
      setLoading(true);
      const res = await chitOnboardByDateRange(start, end, "chit");

      let chitsData = [];

      if (Array.isArray(res.data)) {
        chitsData = res.data.map((item) => ({
          ...item.chitData, // This includes chitMemberList as it's part of chitData
          category: item.category,
          createdAt: item.createdAt,
          name: item.name,
          pupilEmail: item.pupilEmail,
        }));
      } else {
        chitsData = res.data.chitData
          ? [
              {
                ...res.data.chitData, // This includes chitMemberList as it's part of chitData
                category: res.data.category,
                createdAt: res.data.createdAt,
                name: res.data.name,
                pupilEmail: res.data.pupilEmail,
              },
            ]
          : [res.data];
      }

      setChits(chitsData);
    } catch (err) {
      toast.error("Error fetching chit data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Default fetch for current year when component mounts
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const defaultStart = `${currentYear}-01-01`;
    const defaultEnd = `${currentYear}-12-31`;

    if (!startDate && !endDate) {
      fetchChits(defaultStart, defaultEnd);
    }
  }, []);

  // Manual fetch button
  const handleFetchClick = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    fetchChits(startDate, endDate);
  };

  const handleViewDetails = (chit) => {
    setSelectedChit(chit);
    setIsModalOpen(true);
  };

  const handleViewMembers = (chit) => {
    setSelectedChit(chit);
    setIsMemberModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedChit(null);
  };

  const handleCloseMemberModal = () => {
    setIsMemberModalOpen(false);
    setSelectedChit(null);
  };

  return (
    <>
      <div className="flex flex-wrap">
        <div className="w-full">
          <ul
            className="flex mb-0 list-none flex-wrap pt-3 pb-4 flex-row"
            role="tablist"
          >
            <li className="-mb-px mr-2 last:mr-0 flex-auto text-center">
              <a
                className={
                  "text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal " +
                  (openTab === 1
                    ? "text-white bg-" + color + "-600"
                    : "text-gray-700 bg-gray-100 hover:bg-gray-200")
                }
                onClick={(e) => {
                  e.preventDefault();
                  setOpenTab(1);
                }}
                href="#link1"
                role="tablist"
              >
                GVG
              </a>
            </li>
            <li className="-mb-px mr-2 last:mr-0 flex-auto text-center">
              <a
                className={
                  "text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal " +
                  (openTab === 2
                    ? "text-white bg-" + color + "-600"
                    : "text-gray-700 bg-gray-100 hover:bg-gray-200")
                }
                onClick={(e) => {
                  e.preventDefault();
                  setOpenTab(2);
                }}
                href="#link2"
                role="tablist"
              >
                Orders
              </a>
            </li>
            <li className="-mb-px mr-2 last:mr-0 flex-auto text-center">
              <a
                className={
                  "text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal " +
                  (openTab === 3
                    ? "text-white bg-" + color + "-600"
                    : "text-gray-700 bg-gray-100 hover:bg-gray-200")
                }
                onClick={(e) => {
                  e.preventDefault();
                  setOpenTab(3);
                }}
                href="#link3"
                role="tablist"
              >
                Plans
              </a>
            </li>
            <li className="-mb-px mr-2 last:mr-0 flex-auto text-center">
              <a
                className={
                  "text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal " +
                  (openTab === 4
                    ? "text-white bg-" + color + "-600"
                    : "text-gray-700 bg-gray-100 hover:bg-gray-200")
                }
                onClick={(e) => {
                  e.preventDefault();
                  setOpenTab(4);
                }}
                href="#link4"
                role="tablist"
              >
                Order Plans
              </a>
            </li>
            <li className="-mb-px mr-2 last:mr-0 flex-auto text-center">
              <a
                className={
                  "text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal " +
                  (openTab === 5
                    ? "text-white bg-" + color + "-600"
                    : "text-gray-700 bg-gray-100 hover:bg-gray-200")
                }
                onClick={(e) => {
                  e.preventDefault();
                  setOpenTab(5);
                }}
                href="#link5"
                role="tablist"
              >
                <i className="fas fa-coins text-base mr-1"></i> Chits
              </a>
            </li>
          </ul>

          {/* ==== Tab Content ==== */}
          <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
            <div className="px-4 py-5 flex-auto">
              <div className="tab-content tab-space">
                <div className={openTab === 1 ? "block" : "hidden"} id="link1">
                  <AdminGvg />
                </div>
                <div className={openTab === 2 ? "block" : "hidden"} id="link2">
                  <AdminDashboardOrder />
                </div>
                <div className={openTab === 3 ? "block" : "hidden"} id="link3">
                  <AdminPlanDashboard />
                </div>
                <div className={openTab === 4 ? "block" : "hidden"} id="link4">
                  <AdminOrderPlan />
                </div>

                {/* ==== Chits Tab ==== */}
                <div className={openTab === 5 ? "block" : "hidden"} id="link5">
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">
                      Chits Management
                    </h2>

                    {/* Date Filter */}
                    <div className="flex flex-col md:flex-row gap-4 items-center mb-6 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="border border-gray-300 rounded-lg p-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="border border-gray-300 rounded-lg p-2"
                        />
                      </div>
                      <button
                        onClick={handleFetchClick}
                        disabled={loading}
                        className="px-6 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
                      >
                        {loading ? "Loading..." : "Fetch Data"}
                      </button>
                    </div>

                    {/* Chits Table */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Title
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Value
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Members
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Start Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              End Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {chits.length > 0 ? (
                            chits.map((chit, index) => (
                              <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {chit.chitTitle}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  ₹{chit.chitValue?.toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {chit.members}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {formatDisplayDate(chit.startDate)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {formatDisplayDate(chit.endDate)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleViewDetails(chit)}
                                      className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                                    >
                                      View Details
                                    </button>
                                    <button
                                      onClick={() => handleViewMembers(chit)}
                                      className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                                    >
                                      Member List
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan="6"
                                className="px-6 py-4 text-center text-gray-500"
                              >
                                No chit data available. Select a date range and
                                fetch data.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Chit Details Modal */}
                    {isModalOpen && selectedChit && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                          <div className="bg-blue-500 p-6 text-white rounded-t-xl">
                            <div className="flex justify-between items-start">
                              <div>
                                <h2 className="text-2xl font-bold">
                                  {selectedChit.chitTitle}
                                </h2>
                                <p className="mt-1">
                                  Chit Value: ₹
                                  {selectedChit.chitValue?.toLocaleString()}
                                </p>
                              </div>
                              <button
                                onClick={handleCloseModal}
                                className="text-white hover:text-blue-200 text-2xl"
                              >
                                &times;
                              </button>
                            </div>
                          </div>

                          <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-700 mb-2">
                                  Start Date
                                </h3>
                                <p>{formatDisplayDate(selectedChit.startDate)}</p>
                              </div>
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-700 mb-2">
                                  End Date
                                </h3>
                                <p>{formatDisplayDate(selectedChit.endDate)}</p>
                              </div>
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-700 mb-2">
                                  Members
                                </h3>
                                <p>{selectedChit.members}</p>
                              </div>
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-700 mb-2">
                                  Months
                                </h3>
                                <p>{selectedChit.months}</p>
                              </div>
                            </div>

                            {selectedChit.scheduleData &&
                              selectedChit.scheduleData.length > 0 && (
                                <div className="mb-6">
                                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                    Schedule Details
                                  </h3>
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full bg-white border border-gray-200">
                                      <thead>
                                        <tr className="bg-gray-100">
                                          <th className="py-2 px-4 border-b">
                                            Month
                                          </th>
                                          <th className="py-2 px-4 border-b">
                                            Full Chit Non-Bidder
                                          </th>
                                          <th className="py-2 px-4 border-b">
                                            Half Non-Chit
                                          </th>
                                          <th className="py-2 px-4 border-b">
                                            Full Chit
                                          </th>
                                          <th className="py-2 px-4 border-b">
                                            Half Chit
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {selectedChit.scheduleData.map(
                                          (data, index) => (
                                            <tr
                                              key={index}
                                              className={
                                                index % 2 === 0
                                                  ? "bg-gray-50"
                                                  : "bg-white"
                                              }
                                            >
                                              <td className="py-2 px-4 border-b text-center">
                                                {data.month}
                                              </td>
                                              <td className="py-2 px-4 border-b text-center">
                                                {data.fullNonchit || "-"}
                                              </td>
                                              <td className="py-2 px-4 border-b text-center">
                                                {data.halfNonChit || "-"}
                                              </td>
                                              <td className="py-2 px-4 border-b text-center">
                                                {data.fullChit || "-"}
                                              </td>
                                              <td className="py-2 px-4 border-b text-center">
                                                {data.halfChit || "-"}
                                              </td>
                                            </tr>
                                          )
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                          </div>

                          <div className="bg-gray-100 p-4 rounded-b-xl flex justify-end">
                            <button
                              onClick={handleCloseModal}
                              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-400 transition-colors"
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Chit Member List Modal */}
                    {isMemberModalOpen && selectedChit && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                          <div className="bg-green-500 p-6 text-white rounded-t-xl">
                            <div className="flex justify-between items-start">
                              <div>
                                <h2 className="text-2xl font-bold">
                                  Member List - {selectedChit.chitTitle}
                                </h2>
                                <p className="mt-1">
                                  Total Members: {selectedChit.chitMemberList?.length || 0}
                                </p>
                              </div>
                              <button
                                onClick={handleCloseMemberModal}
                                className="text-white hover:text-green-200 text-2xl"
                              >
                                &times;
                              </button>
                            </div>
                          </div>

                          <div className="p-6">
                            {selectedChit.chitMemberList && selectedChit.chitMemberList.length > 0 ? (
                              <div className="space-y-3">
                                {selectedChit.chitMemberList.map((email, index) => (
                                  <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                                    <span className="w-8 h-8 flex items-center justify-center bg-green-100 text-green-800 rounded-full mr-3">
                                      {index + 1}
                                    </span>
                                    <span className="text-gray-700">{email}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                No members found for this chit.
                              </div>
                            )}
                          </div>

                          <div className="bg-gray-100 p-4 rounded-b-xl flex justify-end">
                            <button
                              onClick={handleCloseMemberModal}
                              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-400 transition-colors"
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default function AdminTab() {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">
          Admin Control Panel
        </h1>
        <p className="text-gray-600">
          Manage all aspects of your application from this dashboard
        </p>
      </div>
      <Tabs color="blue" />
    </div>
  );
}